import { useEffect, useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Search, ShoppingCart, ArrowUpDown } from 'lucide-react'
import { useMenuStore } from '../../core/stores/menuStore'
import { useTranslation } from '../../core/hooks/useTranslation'
import { useBusinessColor } from '../../core/hooks/useBusinessColor'
import { PublicMenuItem } from '../../core/models/menu'
import { Card, CardContent } from '../../components/ui/Card'
import Input from '../../components/ui/Input'
import Badge from '../../components/ui/Badge'
import MenuItemCard from '../../components/menu/MenuItemCard'
import CategoryTabs from '../../components/menu/CategoryTabs'

type SortOption = 'popular' | 'price-low' | 'price-high' | 'name-asc' | 'name-desc'

export default function MenuHomePage() {
  const { slug } = useParams()
  const { isRTL } = useTranslation()
  const { color, opacity15 } = useBusinessColor()
  const {
    menu,
    categories,
    selectedCategory,
    searchTerm,
    setSelectedCategory,
    setSearchTerm,
    searchItems
  } = useMenuStore()

  const [searchResults, setSearchResults] = useState<PublicMenuItem[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [sortBy, setSortBy] = useState<SortOption>('price-low')

  // Data is already fetched in MenuPage, no need to fetch again

  useEffect(() => {
    const performSearch = async () => {
      if (searchTerm.trim() && slug) {
        setIsSearching(true)
        try {
          const results = await searchItems(slug, searchTerm)
          setSearchResults(results)
        } finally {
          setIsSearching(false)
        }
      } else {
        setSearchResults([])
      }
    }

    const debounceTimer = setTimeout(performSearch, 300)
    return () => clearTimeout(debounceTimer)
  }, [searchTerm, slug, searchItems])

  // Data is already loaded in MenuPage, so we can assume it's available
  if (!menu) {
    return null
  }

  // Get base items
  const baseItems = searchTerm.trim() 
    ? searchResults 
    : selectedCategory?.items || []

  // Apply sorting
  const displayItems = useMemo(() => {
    let sorted = [...baseItems]

    // Apply sorting
    switch (sortBy) {
      case 'price-low':
        sorted.sort((a, b) => a.price - b.price)
        break
      case 'price-high':
        sorted.sort((a, b) => b.price - a.price)
        break
      case 'name-asc':
        sorted.sort((a, b) => a.name.localeCompare(b.name, isRTL ? 'ar' : 'en'))
        break
      case 'name-desc':
        sorted.sort((a, b) => b.name.localeCompare(a.name, isRTL ? 'ar' : 'en'))
        break
      default:
        // Keep original order
        break
    }

    return sorted
  }, [baseItems, sortBy, isRTL])

  return (
    <>
      <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8">
        {/* Search and Sort Bar */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search Bar */}
            <div className="relative flex-1">
              <div className={`absolute inset-y-0 ${isRTL ? 'right-0' : 'left-0'} ${isRTL ? 'pr-3' : 'pl-3'} flex items-center pointer-events-none`}>
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder={isRTL ? 'ابحث في القائمة...' : 'Search menu...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`${isRTL ? 'pr-10' : 'pl-10'} w-full h-11 bg-white border border-gray-200 rounded-lg text-sm`}
                onFocus={(e) => {
                  e.target.style.borderColor = color
                  e.target.style.boxShadow = `0 0 0 2px ${opacity15}`
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb'
                  e.target.style.boxShadow = 'none'
                }}
              />
              {isSearching && (
                <div className={`absolute inset-y-0 ${isRTL ? 'left-0' : 'right-0'} ${isRTL ? 'pl-3' : 'pr-3'} flex items-center`}>
                  <div className="loading-spinner w-5 h-5 border-2 border-gray-300" style={{ borderTopColor: color }}></div>
                </div>
              )}
            </div>

            {/* Sort Control */}
            {((searchTerm.trim() && displayItems.length > 0) || (selectedCategory && selectedCategory.items.length > 0)) && (
              <div className="flex items-center gap-2 sm:w-auto w-full">
                <ArrowUpDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="flex-1 sm:flex-initial sm:w-auto w-full h-11 px-3 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 cursor-pointer"
                  onFocus={(e) => {
                    e.target.style.borderColor = color
                    e.target.style.boxShadow = `0 0 0 2px ${opacity15}`
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb'
                    e.target.style.boxShadow = 'none'
                  }}
                >
                  <option value="price-low">{isRTL ? 'السعر: من الأقل للأعلى' : 'Price: Low to High'}</option>
                  <option value="price-high">{isRTL ? 'السعر: من الأعلى للأقل' : 'Price: High to Low'}</option>
                  <option value="name-asc">{isRTL ? 'الاسم: أ-ي' : 'Name: A-Z'}</option>
                  <option value="name-desc">{isRTL ? 'الاسم: ي-أ' : 'Name: Z-A'}</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Search Results */}
        {searchTerm.trim() ? (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-xl sm:text-2xl font-bold text-stone-900">
                  {isRTL ? `نتائج البحث عن "${searchTerm}"` : `Search results for "${searchTerm}"`}
                </h2>
                <Badge variant="primary" size="lg" className="text-sm font-semibold">
                  {displayItems.length} {isRTL ? 'نتيجة' : 'results'}
                </Badge>
              </div>
            </div>

            {searchResults.length === 0 ? (
              <Card variant="elevated" className="animate-slide-up border-stone-200 shadow-lg">
                <CardContent className="text-center py-12 sm:py-16">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-stone-200 to-stone-300 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-inner">
                    <Search className="w-8 h-8 sm:w-10 sm:h-10 text-stone-500" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-stone-900 mb-2">
                    {isRTL ? 'لا توجد نتائج' : 'No Results Found'}
                  </h3>
                  <p className="text-stone-600 text-sm sm:text-base">
                    {isRTL ? 'جرب البحث بكلمات مختلفة' : 'Try searching with different keywords'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {searchResults.map((item, index) => (
                  <div key={item.id} className="animate-slide-up" style={{ animationDelay: `${index * 50}ms` }}>
                    <MenuItemCard item={item} />
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Enhanced Category Tabs */}
            <div className="mb-4 sm:mb-6">
              <CategoryTabs
                categories={categories}
                selectedCategory={selectedCategory}
                onSelectCategory={setSelectedCategory}
              />
            </div>


            {/* Menu Items */}
            {selectedCategory ? (
              <div className="animate-fade-in">
                {displayItems.length === 0 ? (
                  <Card variant="elevated" className="animate-slide-up border-stone-200 shadow-lg">
                    <CardContent className="text-center py-12 sm:py-16">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-stone-200 to-stone-300 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-inner">
                        <ShoppingCart className="w-8 h-8 sm:w-10 sm:h-10 text-stone-500" />
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold text-stone-900 mb-2">
                        {isRTL ? 'لا توجد عناصر' : 'No Items'}
                      </h3>
                      <p className="text-stone-600 text-sm sm:text-base">
                        {isRTL ? 'لا توجد عناصر في هذه الفئة حالياً' : 'No items in this category currently'}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {displayItems.map((item, index) => (
                      <div key={item.id} className="animate-slide-up" style={{ animationDelay: `${index * 50}ms` }}>
                        <MenuItemCard item={item} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Card variant="elevated" className="animate-slide-up border-stone-200 shadow-lg">
                <CardContent className="text-center py-12 sm:py-16">
                  <div 
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg"
                    style={{
                      background: `linear-gradient(to bottom right, ${color}cc, ${color})`,
                      boxShadow: `0 10px 15px -3px ${opacity15}, 0 4px 6px -2px ${opacity15}`,
                    }}
                  >
                    <ShoppingCart className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-stone-900 mb-2">
                    {isRTL ? 'اختر فئة' : 'Select Category'}
                  </h3>
                  <p className="text-stone-600 text-sm sm:text-base">
                    {isRTL ? 'اختر فئة من الأعلى لعرض العناصر' : 'Select a category above to view items'}
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}

      </div>
    </>
  )
}

